import { IS_DEV_ENV } from "constants/app.constants";
import {
  DEFAULT_CHART_TYPE_PARAM_X_PROPERTIES,
  DEFAULT_CHART_TYPE_PARAM_Y_PROPERTIES
} from "constants/chart.constants";
import { CalculationMethod } from "constants/charts.enums";
import { EntityType } from "constants/entities.enum";
import { createEntityState } from "entities/charts/factory";
import { createMergeWithDefaults } from "entities/utils";
import { EvaChartFeatures } from "types";

import {
  DEFAULT_HIGHLIGHTED_LINE_OPACITY_FEATURE_ENTITY,
  DEFAULT_HIGHLIGHTED_LINE_SIZE_FEATURE_ENTITY,
  DEFAULT_LINE_OPACITY_FEATURE_ENTITY,
  DEFAULT_LINE_SIZE_FEATURE_ENTITY,
  DEFAULT_POINT_OPACITY_FEATURE_ENTITY,
  DEFAULT_POINT_SIZE_FEATURE_ENTITY
} from "./feature";

const TREND_DATE_FEATURES: Partial<EvaChartFeatures> = {
  screenshot: createEntityState(EntityType.ScreenshotFeature, {
    show: true,
    enabled: true
  }),
  fullScreen: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true
  }),
  datatable: createEntityState(EntityType.ChartDataTableFeature, {
    show: true,
    enabled: true
  }),
  spaghetti: createEntityState(EntityType.ChartsFeature, { show: true }),
  forecast: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true,
    active: true
  }),
  source: createEntityState(EntityType.SourceFeature, { show: true, enabled: true }),
  debug: createEntityState(EntityType.ChartsFeature, {
    show: IS_DEV_ENV,
    enabled: IS_DEV_ENV
  }),
  timeStep: createEntityState(EntityType.TimeStepFeature, {
    show: true,
    enabled: true
  }),
  isProducingRateType: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true
  }),
  cutoff: createEntityState(EntityType.CutOffChartFeature, {
    show: true,
    enabled: true
  }),
  shutInMonths: createEntityState(EntityType.ShutInMonthsChartFeature, {
    show: true,
    enabled: true
  }),
  movingAverageDays: createEntityState(EntityType.MovingAverageDaysChartFeature, {
    show: true,
    enabled: true
  }),
  survivorBias: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true,
    active: true
  }),
  useWeightedRatioAverage: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true,
    active: true
  }),
  lockUnits: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true
  }),
  lasso: createEntityState(EntityType.LassoFeature, {
    show: true,
    enabled: true
  }),
  typewells: createEntityState(EntityType.ChartsFeature, {
    show: true
  }),
  normalization: createEntityState(EntityType.NormalizationFeature, {
    show: true
  }),
  chartFocus: createEntityState(EntityType.ChartFocusFeature, {
    show: true,
    enabled: true
  }),
  legend: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true
  }),
  lineSize: {
    ...DEFAULT_LINE_SIZE_FEATURE_ENTITY,
    show: false,
    enabled: false
  },
  lineOpacity: {
    ...DEFAULT_LINE_OPACITY_FEATURE_ENTITY,
    show: false,
    enabled: false
  },
  highlightedLineSize: {
    ...DEFAULT_HIGHLIGHTED_LINE_SIZE_FEATURE_ENTITY,
    show: false,
    enabled: false
  },
  highlightedLineOpacity: {
    ...DEFAULT_HIGHLIGHTED_LINE_OPACITY_FEATURE_ENTITY,
    show: false,
    enabled: false
  },
  pointSize: {
    ...DEFAULT_POINT_SIZE_FEATURE_ENTITY,
    show: false,
    enabled: false
  },
  pointOpacity: {
    ...DEFAULT_POINT_OPACITY_FEATURE_ENTITY,
    show: false,
    enabled: false
  },
  calculationMethod: createEntityState(EntityType.CalculationMethodFeature, {
    properties: {
      method: CalculationMethod.Average
    }
  }),
  chartTypeParam: createEntityState(EntityType.ChartTypeParamFeature, {
    properties: {
      x: DEFAULT_CHART_TYPE_PARAM_X_PROPERTIES,
      y: DEFAULT_CHART_TYPE_PARAM_Y_PROPERTIES
    }
  }),
  bestFitToggle: createEntityState(EntityType.BestFitToggleFeature, {}),
  histogramToggle: createEntityState(EntityType.HistogramToggleFeature, {}),
  jitterPlotToggle: createEntityState(EntityType.JitterPlotToggleFeature, {}),
  quarterSlope: createEntityState(EntityType.SlopeLineFeature, {}),
  unitSlope: createEntityState(EntityType.SlopeLineFeature, {}),
  halfSlope: createEntityState(EntityType.SlopeLineFeature, {}),
  useProbitLineOfBestFitData: createEntityState(EntityType.ChartsFeature, {})
};

export const trendDateFeatures = createMergeWithDefaults(TREND_DATE_FEATURES);
